// The game's sounds (spec FE-24; design system 03-game-ux "Sound and haptics"): each action has one
// sound. A pressed disc clicks, a done stop dings, a knock is an engine cough, the horn is the
// player's own horn (sound.ts), a report blows the conductor's whistle, a wahala sounds the alarm.
// Synthesised with Web Audio, so nothing to download; silent before the first tap, with sound off
// and while Lagos Life has the game paused (all decided by output()). No music: a round is talk.
import { output } from './sound';

type Wave = OscillatorType;

/** One note: a frequency (optionally gliding to another), shaped by a quick attack and a decay. */
function note(at: number, f: number, len: number, o: { wave?: Wave; gain?: number; to?: number } = {}): void {
  const a = output();
  if (!a) return;
  const { ctx, out } = a;
  const t = ctx.currentTime + at;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = o.wave ?? 'sine';
  osc.frequency.setValueAtTime(f, t);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + len);
  const g = o.gain ?? 0.2;
  env.gain.setValueAtTime(0, t);
  env.gain.linearRampToValueAtTime(g, t + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, t + len);
  osc.connect(env).connect(out);
  osc.start(t);
  osc.stop(t + len + 0.02);
}

/** A burst of filtered noise: engine coughs and thumps. */
function noise(at: number, len: number, freq: number, gain = 0.3): void {
  const a = output();
  if (!a) return;
  const { ctx, out } = a;
  const t = ctx.currentTime + at;
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * len), ctx.sampleRate);
  const data = buf.getChannelData(0);
  // the waveform of a sound effect, not game state: Math.random is fine here
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = freq;
  const env = ctx.createGain();
  env.gain.setValueAtTime(gain, t);
  env.gain.exponentialRampToValueAtTime(0.0001, t + len);
  src.connect(filter).connect(env).connect(out);
  src.start(t);
}

export const sfx = {
  /** A HUD disc pressed. */
  click(): void {
    note(0, 1900, 0.04, { wave: 'square', gain: 0.05 });
  },
  /** A stop done: a bright two-note ding. */
  ding(): void {
    note(0, 1319, 0.25, { gain: 0.16 });
    note(0.09, 1760, 0.45, { gain: 0.16 });
  },
  /** Knocked: the engine coughs three times and dies. */
  cough(): void {
    noise(0, 0.12, 700, 0.45);
    noise(0.18, 0.1, 600, 0.35);
    noise(0.36, 0.3, 380, 0.4);
    note(0.36, 110, 0.5, { wave: 'sawtooth', gain: 0.08, to: 55 });
  },
  /** A breakdown reported: the conductor's whistle, a warbling high trill. */
  whistle(): void {
    for (let i = 0; i < 6; i++) note(i * 0.07, i % 2 ? 2350 : 2650, 0.08, { gain: 0.09 });
  },
  /** Wahala: a two-tone alarm, three times. */
  alarm(): void {
    for (let i = 0; i < 3; i++) {
      note(i * 0.36, 740, 0.17, { wave: 'square', gain: 0.07 });
      note(i * 0.36 + 0.18, 554, 0.17, { wave: 'square', gain: 0.07 });
    }
  },
  /** Half a two-part fix done. */
  half(): void {
    note(0, 880, 0.18, { wave: 'triangle', gain: 0.14 });
  },
  /** Wahala cleared: a rising third. */
  fixed(): void {
    note(0, 659, 0.2, { wave: 'triangle', gain: 0.15 });
    note(0.12, 831, 0.35, { wave: 'triangle', gain: 0.15 });
  },
  /** A vote cast in the meeting. */
  vote(): void {
    note(0, 520, 0.08, { wave: 'triangle', gain: 0.1 });
  },
  /** Owa: a keke dropped, a long falling slide and a thump. */
  owa(): void {
    note(0, 660, 0.7, { wave: 'sawtooth', gain: 0.06, to: 90 });
    noise(0.7, 0.25, 220, 0.5);
  },
  /** Nobody dropped (a skip or a tie): two flat notes. */
  nobody(): void {
    note(0, 392, 0.18, { wave: 'triangle', gain: 0.1 });
    note(0.2, 392, 0.25, { wave: 'triangle', gain: 0.1 });
  },
  /** Fares paid at a drop: coins clinking into the box. */
  coins(): void {
    note(0, 2093, 0.07, { wave: 'triangle', gain: 0.1 });
    note(0.06, 2637, 0.09, { wave: 'triangle', gain: 0.1 });
    note(0.13, 3136, 0.16, { wave: 'triangle', gain: 0.09 });
  },
  /** Passengers climb in: the keke settles on its springs. */
  board(): void {
    note(0, 330, 0.09, { wave: 'triangle', gain: 0.1 });
    note(0.08, 440, 0.12, { wave: 'triangle', gain: 0.1 });
  },
  /** Two kekes crash: a thump of metal and glass. */
  crash(): void {
    noise(0, 0.08, 2400, 0.5);
    noise(0.03, 0.28, 300, 0.55);
    note(0, 140, 0.25, { wave: 'square', gain: 0.07, to: 60 });
  },
  /** A power picked up. */
  power(): void {
    for (let i = 0; i < 4; i++) note(i * 0.05, 880 * 2 ** (i / 4), 0.12, { gain: 0.08 });
  },
  /** The round is yours: up the scale. */
  win(): void {
    [523, 659, 784, 1047].forEach((f, i) => {
      note(i * 0.11, f, i === 3 ? 0.6 : 0.2, { wave: 'triangle', gain: 0.16 });
    });
  },
  /** The round went the other way. */
  lose(): void {
    [392, 330, 262].forEach((f, i) => {
      note(i * 0.16, f, i === 2 ? 0.6 : 0.22, { wave: 'triangle', gain: 0.14 });
    });
  },
};
